import {Avatar, Badge, Button, Card, IconButton} from 'platform/components';
import {Box, Heading, HStack, Icon, Link, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import i18n from '@dms/i18n';
import {customerRoutes} from '@dms/routes';

import {composePath, type TestIdProps} from 'shared/web';

import type {
  CustomerInsightsLastContact,
  CustomerInsightsViewModel,
} from '../../types/CustomerInsightsViewModel';
import {
  type CustomerInsightsOpenGroupName,
  getCustomerInsightsContentState,
} from '../../utils/getCustomerInsightsContentState';
import {CustomerInsightsAlertCard} from './components/CustomerInsightsAlertCard';
import {CustomerInsightsClickableRow} from './components/CustomerInsightsClickableRow';
import {CustomerInsightsContactActions} from './components/CustomerInsightsContactActions';
import {CustomerInsightsDateMetadata} from './components/CustomerInsightsDateMetadata';
import {CustomerInsightsHistorySections} from './components/CustomerInsightsHistorySections';
import {CustomerInsightsLink} from './components/CustomerInsightsLink';
import {CustomerInsightsRecordList} from './components/CustomerInsightsRecordList';
import {CustomerInsightsSection} from './components/CustomerInsightsSection';
import {CustomerInsightsSkeletonRow} from './components/CustomerInsightsSkeletonRow';

export interface CustomerInsightsContentProps extends TestIdProps {
  viewModel: CustomerInsightsViewModel;
  isScrollable?: boolean;
  onClose?: VoidFunction;
  titleId?: string;
}

const IDENTITY_SKELETON_HEIGHT = 8;
const SUMMARY_SKELETON_HEIGHT = 8;
const RECORDS_SKELETON_HEIGHT = 8;

const getLastContactChannelKey = (channel: CustomerInsightsLastContact['channel']) =>
  match(channel)
    .with('email', () => 'general.labels.email' as const)
    .with('sms', () => 'general.labels.sms' as const)
    .with('whatsapp', () => 'entity.activityPanel.chat.options.whatsapp' as const)
    .with('call', () => 'entity.task.type.call' as const)
    .exhaustive();

const getLastContactDirectionKey = (direction: CustomerInsightsLastContact['direction']) =>
  match(direction)
    .with('INCOMING', () => 'entity.prescription.labels.incoming' as const)
    .with('OUTGOING', () => 'entity.prescription.labels.outgoing' as const)
    .exhaustive();

const OPEN_GROUP_TITLE_KEYS = {
  openInterests: 'feature.customerInsights.openInterests',
  openBusinessCases: 'feature.customerInsights.openBusinessCases',
  openServiceCases: 'feature.customerInsights.openServiceCases',
} as const satisfies Record<CustomerInsightsOpenGroupName, string>;

export function CustomerInsightsContent(props: CustomerInsightsContentProps) {
  const {
    failedRetries,
    showFailure,
    showPartialFailure,
    showEmptyCustomer,
    showLastContact,
    showAlerts,
    hasAlertsLoading,
    firstLoadingSummarySection,
    openGroups,
    showOpenItems,
    hasOpenItemsLoading,
    openItemsCount,
    showCurrentActivity,
  } = getCustomerInsightsContentState(props.viewModel);
  const customerDetailPath = match(props.viewModel.identity)
    .with({status: 'ready'}, {status: 'degraded'}, ({data}) =>
      composePath(customerRoutes.detail, {params: {id: data.id}})
    )
    .otherwise(() => undefined);
  const openInterestsPath = match(props.viewModel.identity)
    .with({status: 'ready'}, {status: 'degraded'}, ({data}) =>
      composePath(customerRoutes.interests, {params: {id: data.id}})
    )
    .otherwise(() => undefined);
  const openBusinessCasesPath = match(props.viewModel.identity)
    .with({status: 'ready'}, {status: 'degraded'}, ({data}) =>
      composePath(customerRoutes.businessCases, {params: {id: data.id}})
    )
    .otherwise(() => undefined);
  const openServiceCasesPath = match(props.viewModel.identity)
    .with({status: 'ready'}, {status: 'degraded'}, ({data}) =>
      composePath(customerRoutes.serviceCases, {params: {id: data.id}})
    )
    .otherwise(() => undefined);
  // Every listed kind gets its way in, not only a kind whose list was truncated.
  // A kind can hold fewer records than the list shows and still be worth opening.
  const openGroupPaths: Record<CustomerInsightsOpenGroupName, string | undefined> = {
    openInterests: openInterestsPath,
    openBusinessCases: openBusinessCasesPath,
    openServiceCases: openServiceCasesPath,
  };
  const isScrollable = props.isScrollable ?? false;

  return (
    <section aria-busy={props.viewModel.isRefreshing} data-testid={props['data-testid']}>
      <Box backgroundColor="general.white">
        <VStack minWidth={0} maxHeight={isScrollable ? '90vh' : undefined}>
          <Box
            paddingHorizontal={4}
            paddingVertical={2}
            borderBottom="thin solid"
            borderColor="general.separator"
          >
            <HStack spacing={4} align="flex-start" justify="space-between">
              <VStack spacing={3} minWidth={0} grow={1}>
                <Box id={props.titleId}>
                  <Heading size={5} headingLevel={2}>
                    {i18n.t('feature.customerInsights.title')}
                  </Heading>
                </Box>
                <CustomerInsightsSection
                  section={props.viewModel.identity}
                  skeletonHeight={IDENTITY_SKELETON_HEIGHT}
                  skeletonVariant="identity"
                  skeletonPaddingHorizontal={0}
                  skeletonPaddingVertical={0}
                  data-testid="customerInsights-identity"
                >
                  {(identity) => (
                    <VStack spacing={2} minWidth={0}>
                      <Heading size={4} headingLevel={3} overflowWrap="anywhere">
                        {identity.name}
                      </Heading>
                      <CustomerInsightsContactActions identity={identity} />
                    </VStack>
                  )}
                </CustomerInsightsSection>
              </VStack>
              <Show when={customerDetailPath || props.onClose}>
                <HStack spacing={1} align="center">
                  <Show when={customerDetailPath}>
                    <Link
                      href={customerDetailPath}
                      leftIcon="action/open_in_new"
                      target="_blank"
                      rel="noopener noreferrer"
                      ariaLabel={i18n.t('general.actions.viewDetail')}
                    />
                  </Show>
                  <Show when={props.onClose}>
                    <IconButton
                      size="small"
                      icon="navigation/close"
                      aria-label={i18n.t('general.actions.close')}
                      autoFocus
                      onClick={props.onClose}
                    />
                  </Show>
                </HStack>
              </Show>
            </HStack>
          </Box>

          <Box minHeight={0} overflowY={isScrollable ? 'auto' : undefined}>
            <Show when={showFailure && failedRetries.length > 0}>
              <section aria-label={i18n.t('feature.customerInsights.sectionError')}>
                <Box
                  paddingHorizontal={4}
                  paddingVertical={2}
                  borderBottom="thin solid"
                  borderColor="general.separator"
                  backgroundColor="palettes.red.10.100"
                >
                  <HStack spacing={3} align="center" justify="space-between">
                    <Text size="small">{i18n.t('feature.customerInsights.sectionError')}</Text>
                    <Button
                      size="small"
                      variant="link"
                      title={i18n.t('general.actions.retry')}
                      onClick={() => failedRetries.forEach((retry) => retry())}
                    />
                  </HStack>
                </Box>
              </section>
            </Show>

            {showEmptyCustomer ? (
              <Box
                paddingHorizontal={4}
                paddingVertical={2}
                backgroundColor="palettes.neutral.20.100"
              >
                <HStack spacing={4} align="center">
                  <Avatar
                    size="large"
                    variant="square"
                    icon="social/person"
                    color="palettes.blue.20.100"
                    iconColor="palettes.blue.80.100"
                  />
                  <VStack spacing={1}>
                    <Heading size={4}>{i18n.t('feature.customerInsights.emptyTitle')}</Heading>
                    <Text size="small" color="secondary">
                      {i18n.t('entity.activityPanel.activity.noActivity')}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            ) : (
              <>
                <Show when={showCurrentActivity}>
                  <Box backgroundColor="palettes.neutral.20.100">
                    <Show when={showLastContact}>
                      <CustomerInsightsSection
                        section={props.viewModel.lastContact}
                        title={i18n.t('feature.customerInsights.lastContact')}
                        skeletonHeight={SUMMARY_SKELETON_HEIGHT}
                        showSkeleton={firstLoadingSummarySection === 'lastContact'}
                        data-testid="customerInsights-lastContact"
                      >
                        {(lastContact) => {
                          const preview =
                            lastContact.preview ??
                            i18n.t(getLastContactChannelKey(lastContact.channel));

                          return (
                            <Box paddingHorizontal={4} paddingVertical={2}>
                              <VStack spacing={2}>
                                <HStack spacing={3} align="center" justify="space-between" wrap>
                                  <Heading size={5}>
                                    {i18n.t('feature.customerInsights.lastContact')}
                                  </Heading>
                                  <CustomerInsightsDateMetadata
                                    occurredAt={lastContact.occurredAt}
                                    data-testid="customerInsights-lastContact"
                                  />
                                </HStack>
                                <CustomerInsightsClickableRow href={lastContact.href}>
                                  <Card variant="inlineWhite" colorStripe="blue">
                                    <HStack spacing={3} align="center" minWidth={0}>
                                      <Avatar
                                        size="default"
                                        variant="square"
                                        icon="communication/customer_chat"
                                        color="palettes.blue.20.100"
                                        iconColor="palettes.blue.80.100"
                                      />
                                      <VStack spacing={1} minWidth={0} grow={1}>
                                        <CustomerInsightsLink
                                          href={lastContact.href}
                                          title={preview}
                                          size="xSmall"
                                          overflowWrap="anywhere"
                                        />
                                        <HStack spacing={1} align="center" wrap>
                                          <Text size="xSmall" color="secondary">
                                            {i18n.t(getLastContactChannelKey(lastContact.channel))}
                                          </Text>
                                          <Text size="xSmall" color="secondary">
                                            ·{' '}
                                            {i18n.t(
                                              getLastContactDirectionKey(lastContact.direction)
                                            )}
                                          </Text>
                                        </HStack>
                                      </VStack>
                                      <Icon
                                        value="navigation/chevron_right"
                                        size={4}
                                        color="palettes.neutral.500.100"
                                      />
                                    </HStack>
                                  </Card>
                                </CustomerInsightsClickableRow>
                              </VStack>
                            </Box>
                          );
                        }}
                      </CustomerInsightsSection>
                    </Show>

                    <Show when={showAlerts || hasAlertsLoading}>
                      <Box paddingHorizontal={4} paddingVertical={2}>
                        <VStack spacing={2}>
                          <Show when={showAlerts}>
                            <Heading size={5}>{i18n.t('feature.customerInsights.alerts')}</Heading>
                          </Show>
                          <CustomerInsightsSection
                            section={props.viewModel.overdueInvoices}
                            title={i18n.t('feature.customerInsights.overdueInvoice')}
                            skeletonHeight={SUMMARY_SKELETON_HEIGHT}
                            skeletonPaddingHorizontal={0}
                            skeletonPaddingVertical={0}
                            showSkeleton={
                              showAlerts || firstLoadingSummarySection === 'overdueInvoices'
                            }
                            data-testid="customerInsights-overdueInvoices"
                          >
                            {(alert) => (
                              <CustomerInsightsAlertCard
                                alert={alert}
                                title={i18n.t('feature.customerInsights.overdueInvoice')}
                                data-testid="customerInsights-overdueInvoices"
                              />
                            )}
                          </CustomerInsightsSection>
                          <CustomerInsightsSection
                            section={props.viewModel.overdueTasks}
                            title={i18n.t('feature.customerInsights.overdueTask')}
                            skeletonHeight={SUMMARY_SKELETON_HEIGHT}
                            skeletonPaddingHorizontal={0}
                            skeletonPaddingVertical={0}
                            showSkeleton={
                              showAlerts || firstLoadingSummarySection === 'overdueTasks'
                            }
                            data-testid="customerInsights-overdueTasks"
                          >
                            {(alert) => (
                              <CustomerInsightsAlertCard
                                alert={alert}
                                title={i18n.t('feature.customerInsights.overdueTask')}
                                data-testid="customerInsights-overdueTasks"
                              />
                            )}
                          </CustomerInsightsSection>
                        </VStack>
                      </Box>
                    </Show>

                    <Show when={showOpenItems || hasOpenItemsLoading}>
                      <Box paddingHorizontal={4} paddingVertical={2}>
                        <VStack spacing={3}>
                          <Show when={showOpenItems}>
                            <HStack spacing={2} align="center">
                              <Heading size={5} headingLevel={5}>
                                {i18n.t('general.labels.open')}
                              </Heading>
                              <Show when={openItemsCount !== undefined && openItemsCount > 0}>
                                <Badge
                                  size="small"
                                  colorScheme="blue"
                                  value={openItemsCount ?? 0}
                                />
                              </Show>
                            </HStack>
                          </Show>
                          {openGroups.map((group) => (
                            <VStack key={group.name} spacing={2}>
                              <HStack spacing={2} align="center" justify="space-between" wrap>
                                <HStack spacing={2} align="center">
                                  <Heading size={6} headingLevel={6}>
                                    {i18n.t(OPEN_GROUP_TITLE_KEYS[group.name])}
                                  </Heading>
                                  <Show when={group.total !== undefined && group.total > 0}>
                                    <Badge
                                      size="small"
                                      variant="subtle"
                                      colorScheme="blue"
                                      value={group.total ?? 0}
                                    />
                                  </Show>
                                </HStack>
                                <Show when={openGroupPaths[group.name]}>
                                  <Button
                                    size="small"
                                    variant="link"
                                    title={i18n.t('general.actions.viewAll')}
                                    to={openGroupPaths[group.name] ?? ''}
                                    aria-label={`${i18n.t('general.actions.viewAll')}: ${i18n.t(OPEN_GROUP_TITLE_KEYS[group.name])}`}
                                    data-testid={`customerInsights-${group.name}-viewAll`}
                                  />
                                </Show>
                              </HStack>
                              <CustomerInsightsRecordList records={group.records} />
                            </VStack>
                          ))}
                          <Show when={hasOpenItemsLoading}>
                            <CustomerInsightsSkeletonRow skeletonHeight={RECORDS_SKELETON_HEIGHT} />
                          </Show>
                        </VStack>
                      </Box>
                    </Show>
                  </Box>
                </Show>

                <CustomerInsightsHistorySections viewModel={props.viewModel} />
              </>
            )}

            <Show when={showPartialFailure}>
              <section aria-label={i18n.t('feature.customerInsights.sectionError')}>
                <Box
                  paddingHorizontal={4}
                  paddingVertical={2}
                  backgroundColor="palettes.red.10.100"
                >
                  <HStack spacing={3} align="center" justify="space-between">
                    <Text size="xSmall" color="secondary">
                      {i18n.t('feature.customerInsights.sectionError')}
                    </Text>
                    <Button
                      size="small"
                      variant="link"
                      title={i18n.t('general.actions.retry')}
                      onClick={() => failedRetries.forEach((retry) => retry())}
                    />
                  </HStack>
                </Box>
              </section>
            </Show>
          </Box>
        </VStack>
      </Box>
    </section>
  );
}
